<script setup lang="ts">
import { DropdownMenuItem } from 'reka-ui';
import { type StyleValue, useAttrs } from 'vue';

import KaLink from '@/kasten-components/components/KaLink/KaLink.vue';
import { cnMerge } from '@/utils/utils.js';

import { injectMenuContext } from './context';
import type { KaLinkMenuItemProps, KaMenuItemProps } from './types';

export type {
  KaBaseMenuItemProps,
  KaButtonMenuItemProps,
  KaLinkMenuItemProps,
  KaMenuItemProps,
} from './types';

defineOptions({ name: 'KaMenuItem', inheritAttrs: false });

const emit = defineEmits<{
  (e: 'click', label?: string): void;
}>();

const props = withDefaults(defineProps<KaMenuItemProps>(), {
  as: 'button',
  disabled: false,
  buttonClasses: undefined,
});

const attrs = useAttrs();

const menu = injectMenuContext();
const menuDataTestid = menu?.dataTestid || undefined;
const dataTestid =
  (attrs['data-testid'] as string) ||
  (menuDataTestid?.value ? `${menuDataTestid.value}-menu-item-${props.label}` : undefined);

const handleClick = () => {
  if (props.disabled) return;

  // Move focus back to the menu trigger before running the action. If the action
  // opens a dialog, reka records the currently-focused element as the dialog's
  // focus-restore target; without this, that element is this menu item, which
  // unmounts when the menu closes, so focus falls through to <body> on close.
  // Focusing the persistent trigger first makes focus return there instead. (K10-36133)
  menu?.focusTrigger?.();

  if (props.onClick) {
    props.onClick(props.label);
  } else {
    emit('click', props.label);
  }
};
</script>

<template>
  <!-- prettier-ignore -->
  <li
    v-if="!props.hidden"
    :data-testid="`${dataTestid}-container`"
    :class="cnMerge('w-full list-none py-0.5 m-0 px-0', props.containerClasses)"
    :style="(attrs.style as StyleValue)"
    role="presentation"
  >
    <DropdownMenuItem
      as="template"
      :disabled="props.disabled"
      :data-testid="dataTestid"
    >
      <component
        :is="props.as === 'button' ? 'button' : KaLink"
        :type="props.as === 'button' ? 'button' : undefined"
        :class="
          cnMerge(
            'group inline-flex w-full items-center px-4 py-2 text-left text-sm text-inherit dark:text-inherit font-normal hover:no-underline focus:no-underline hover:text-inherit dark:hover:text-inherit focus:text-inherit hover:bg-gray-100 hover:dark:bg-navy-600',
            {
              
              'text-red-600 dark:text-red-300': props.danger,
              'cursor-not-allowed opacity-50': props.disabled,
            },
            props.as === 'button' && props.buttonClasses,
            props.as === 'link' && (props as KaLinkMenuItemProps).linkProps.class,
          )
        "
        v-bind="props.as === 'link' ? (props as KaLinkMenuItemProps).linkProps : undefined"
        @click.stop="handleClick"
      >
        <span
          v-if="$slots.left || props.left"
          class="mr-2 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center"
          aria-hidden="true"
        >
          <slot name="left">
            <component
              :is="props.left.icon"
              v-if="props.left"
              :class="['h-full w-full', props.left.classes]"
              tabindex="-1"
            />
          </slot>
        </span>

        <span
          v-if="$slots.default || props.label"
          class="block w-full truncate"
          :title="props.label"
        >
          <slot :label="props.label">
            <span :aria-hidden="!!props.srOnlyLabel">{{ props.label }}</span>
            <span v-if="!!$props.srOnlyLabel" class="sr-only">{{ props.srOnlyLabel }}</span>
          </slot>
        </span>

        <span
          v-if="$slots.right || props.right"
          class="ml-2 h-5 w-5 flex-shrink-0"
          aria-hidden="true"
        >
          <slot name="right">
            <component
              :is="props.right.icon"
              v-if="props.right"
              :class="['h-full w-full', props.right.classes]"
              tabindex="-1"
            />
          </slot>
        </span>
      </component>
    </DropdownMenuItem>
  </li>
</template>
